/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/pat/EnterprisePatPanel" />
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { showConfirmationDialog } from "Wit/Settings/BoardsExternalIntegration/components/common";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import { EnterpriseServerUrl } from "Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/EnterpriseServerUrl";
import { IEnterprisePatPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/pat/EnterprisePatPanel.Props";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { createEnterprisePatRequest, isValidEnterpriseUrl } from "Wit/Settings/BoardsExternalIntegration/utils";

export interface IEnterprisePatPanelState {
    isDirty: boolean;
    accceptUntrustedSsl: boolean;
    token: string;
    url: string;
    urlErrorMessage?: string;
    connectionErrorMessage?: string;
    loadingSpinnerMessage?: string;
}

export class EnterprisePatPanel extends VssComponent<IEnterprisePatPanelProps, IEnterprisePatPanelState> {
    private _isMounted = false;
    private _isConfirmationDialogOpened = false;

    constructor(props: IEnterprisePatPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            isDirty: false,
            accceptUntrustedSsl: false,
            token: "",
            url: ""
        };
    }

    public render() {
        return (
            <BasePanel
                onDismiss={this._onDismiss}
                onDismissErrorMessage={this._onDismissErrorMessage}
                title={Resources.EnterprisePat_Title}
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                className="form-panel"
                errorMessage={this.state.connectionErrorMessage}
                loadingSpinnerMessage={this.state.loadingSpinnerMessage}
            >
                <div className="form-panel-content">
                    <EnterpriseServerUrl
                        url={this.state.url}
                        acceptUntrustedSsl={this.state.accceptUntrustedSsl}
                        errorMessage={this.state.urlErrorMessage}
                        onEnterpriseUrlInputChanged={this._onEnterpriseUrlInputChanged}
                        onSslChange={this._onSslCheckBoxChanged}
                        onKeyDown={this._onKeyDown}
                    />
                    <div className="input-label fontWeightSemiBold">{Resources.PAT}</div>
                    <TextField
                        ariaLabel={Resources.PAT}
                        className="input-field"
                        onChange={this._onTokenInputChanged}
                        placeholder={Resources.GitHubAuthToken_Placeholder}
                        inputType="password"
                        value={this.state.token}
                        onKeyDown={this._onKeyDown}
                    />
                    <div className="input-description">
                        {Resources.Pat_description}
                        <br />
                        {GitHubConnectionConstants.PatScopes}
                    </div>
                </div>
                <Link className="form-panel-footer" target="_blank" href={GitHubConnectionConstants.PersonalAccessTokenLink}>
                    {Resources.EnterprisePat_LearnMoreAboutPat}
                </Link>
            </BasePanel>
        );
    }

    public componentDidMount(): void {
        this._isMounted = true;
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
    }

    private _onEnterpriseUrlInputChanged = (value: string): void => {
        const isValidUrlInput = isValidEnterpriseUrl(value);

        this.setState({
            isDirty: true,
            url: value,
            urlErrorMessage: isValidUrlInput ? undefined : Resources.EnterprisePat_InvalidUrlInput,
            connectionErrorMessage: undefined
        });
    };

    private _onTokenInputChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        this.setState({
            isDirty: true,
            token: value,
            connectionErrorMessage: undefined
        });
    };

    private _onSslCheckBoxChanged = (checked: boolean): void => {
        this.setState({
            isDirty: true,
            accceptUntrustedSsl: checked,
            connectionErrorMessage: undefined
        });
    };

    private _onDismissErrorMessage = () => {
        this.setState({ connectionErrorMessage: undefined });
    };

    private _renderHeader = () => {
        return <div>{Resources.EnterprisePat_SubTitle}</div>;
    };

    private _renderFooter = (): JSX.Element => {
        return (
            <div className="align-right">
                <Button text={Resources.Cancel} onClick={this.props.onDismiss} />
                <Button primary={true} text={Resources.Connect} onClick={this._onConnectButtonClick} disabled={!this._canConnect()} />
            </div>
        );
    };

    private _onDismiss = (): void => {
        if (!this.state.isDirty) {
            this.props.onDismiss();
        } else {
            showConfirmationDialog(
                this.context,
                Resources.ConfirmationClosePanelMessage,
                Resources.ConfirmationClosePanelActionText,
                Resources.ConfirmationClosePanelTitle,
                this.props.onDismiss,
                this._onConfirmationDialogCanceled
            );
            this._isConfirmationDialogOpened = true;
            this.setState({ loadingSpinnerMessage: undefined });
        }
    };

    private _onConfirmationDialogCanceled = (): void => {
        this._isConfirmationDialogOpened = false;
    };

    private _onConnectButtonClick = () => {
        this.setState({ loadingSpinnerMessage: Resources.EnterprisePat_SpinnerConnectingAccountText });
        this._authenticate();
    };

    private _canConnect = (): boolean => {
        const { url, token, connectionErrorMessage, urlErrorMessage } = this.state;
        return !!(url && token) && !urlErrorMessage && !connectionErrorMessage;
    };

    private _authenticate = async () => {
        const { existingServiceConnections, onConnectionCreated } = this.props;
        const { url, token, accceptUntrustedSsl } = this.state;

        try {
            const serviceEndpoint = await createEnterprisePatRequest(
                this.context.pageContext,
                token,
                existingServiceConnections,
                url,
                accceptUntrustedSsl
            );
            this._shouldProceed() && onConnectionCreated(serviceEndpoint);
        } catch (error) {
            this._shouldProceed() &&
                this.setState({
                    loadingSpinnerMessage: undefined,
                    connectionErrorMessage: error && error.message
                });
        }
    };

    private _shouldProceed(): boolean {
        return this._isMounted && !this._isConfirmationDialogOpened;
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.keyCode === KeyCode.enter && this._canConnect()) {
            this._onConnectButtonClick();
        }
    };
}
